/**
 * Integration model (ADR-0011) helpers for the node editor's Action
 * switcher. Pure functions — kept out of the heavy NodeEditorDrawer so
 * they're unit-testable without pulling Monaco/query deps.
 */
import type { CatalogNode } from "@/lib/api-workflows";

/** The sibling **Actions** (not Triggers) of an integration, in catalog
 *  order. Empty when the node isn't part of an integration. */
export function siblingActionsFor(
  catalog: CatalogNode[],
  integrationId: string | null | undefined,
): CatalogNode[] {
  if (!integrationId) return [];
  return catalog.filter((n) => n.integration === integrationId && !n.is_trigger);
}

/** When switching a node to another Action of the same integration, carry
 *  over only the config fields that exist in the NEW action's schema
 *  (n8n keeps shared parameters). Fields unique to the old action are
 *  dropped; fields new to the target fall back to their schema defaults
 *  when the form renders. */
export function carryConfigToAction(
  oldConfig: Record<string, unknown>,
  newSpec: CatalogNode | undefined,
): Record<string, unknown> {
  const newProps =
    ((newSpec?.config_schema as { properties?: Record<string, unknown> } | undefined)
      ?.properties) ?? {};
  return Object.fromEntries(
    Object.entries(oldConfig).filter(([k]) => k in newProps),
  );
}
